/**
 * Main Application Component
 * Root component that sets up routing and providers
 */
import React from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from '@/app/store';
import { ThemeProvider } from '@/app/contexts/ThemeContext';
import { HeaderActionsProvider } from '@/app/contexts/HeaderActionsContext';
import { AuthProvider } from '@/app/contexts/AuthContext';
import ProtectedRoute from '@/components/common/ProtectedRoute';
import MainLayout from '@/components/layout/MainLayout';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import TransactionList from '@/pages/Transactions/TransactionList';
import TransactionDetail from '@/pages/Transactions/TransactionDetail';
import RewardList from '@/pages/Rewards/RewardList';
import RewardDetail from '@/pages/Rewards/RewardDetail';
import RewardEdit from '@/pages/Rewards/RewardEdit';
import RewardCreate from '@/pages/Rewards/RewardCreate';
import PartnerList from '@/pages/Partners/PartnerList';
import PartnerDetail from '@/pages/Partners/PartnerDetail';
import PartnerEdit from '@/pages/Partners/PartnerEdit';
import PartnerCreate from '@/pages/Partners/PartnerCreate';
import OfferList from '@/pages/Offers/OfferList';
import OfferDetail from '@/pages/Offers/OfferDetail';
import OfferEdit from '@/pages/Offers/OfferEdit';
import OfferCreate from '@/pages/Offers/OfferCreate';
import FulfillmentList from '@/pages/Fulfillments/FulfillmentList';
import FulfillmentDetail from '@/pages/Fulfillments/FulfillmentDetail';
import JobsList from '@/pages/Jobs/JobsList';
import BenepassTransferSummary from '@/pages/BenepassTransfers/BenepassTransferSummary';
import BenepassTransferList from '@/pages/BenepassTransfers/TransferList';
import IncommCardsSummary from '@/pages/IncommCards/IncommCardsSummary';
import IncommCardsList from '@/pages/IncommCards/IncommCardsList';
import IncommCardDetail from '@/pages/IncommCards/IncommCardDetail';
import PaperJournalPage from '@/pages/PaperJournal/PaperJournalPage';
import PaperJournalUploadDetail from '@/pages/PaperJournal/PaperJournalUploadDetail/PaperJournalUploadDetail';
import MarketingBannerList from '@/pages/MarketingBanners/MarketingBannerList';
import MarketingBannerCreate from '@/pages/MarketingBanners/MarketingBannerCreate';
import MarketingBannerEdit from '@/pages/MarketingBanners/MarketingBannerEdit';
import { UserDashboard } from '@/pages/Users/UserDashboard/index';
import SecurityUsers from '@/pages/Security/SecurityUsers';
import SecurityRoles from '@/pages/Security/SecurityRoles';
import Unauthorized from '@/pages/Unauthorized';
import '@/styles/global.scss';

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <ThemeProvider>
        <AuthProvider>
          <HashRouter>
            <HeaderActionsProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/unauthorized" element={<Unauthorized />} />
              
              {/* Protected routes - require authentication and valid role */}
              <Route path="/" element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }>
                <Route index element={<Dashboard />} />
                {/* User routes */}
                <Route path="users" element={<UserDashboard />} />
                <Route path="users/:id" element={<UserDashboard />} />
                {/* Transaction routes */}
                <Route path="transactions" element={<TransactionList />} />
                <Route path="transactions/:id" element={<TransactionDetail />} />
                {/* Rewards Catalog routes */}
                <Route path="rewards" element={<RewardList />} />
                <Route path="rewards/new" element={<RewardCreate />} />
                <Route path="rewards/:id" element={<RewardDetail />} />
                <Route path="rewards/:id/edit" element={<RewardEdit />} />
                {/* Partner routes */}
                <Route path="partners" element={<PartnerList />} />
                <Route path="partners/new" element={<PartnerCreate />} />
                <Route path="partners/:id" element={<PartnerDetail />} />
                <Route path="partners/:id/edit" element={<PartnerEdit />} />
                {/* Offer routes */}
                <Route path="offers" element={<OfferList />} />
                <Route path="offers/new" element={<OfferCreate />} />
                <Route path="offers/:id" element={<OfferDetail />} />
                <Route path="offers/:id/edit" element={<OfferEdit />} />
                {/* Fulfillment routes */}
                <Route path="fulfillments" element={<FulfillmentList />} />
                <Route path="fulfillments/:id" element={<FulfillmentDetail />} />
                <Route
                  path="jobs"
                  element={
                    <ProtectedRoute requiredPermission="jobs:read">
                      <JobsList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="benepass-transfers/list"
                  element={
                    <ProtectedRoute requiredPermission="integrations:read">
                      <BenepassTransferList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="benepass-transfers"
                  element={
                    <ProtectedRoute requiredPermission="integrations:read">
                      <BenepassTransferSummary />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="incomm-cards/list"
                  element={
                    <ProtectedRoute requiredPermission="integrations:read">
                      <IncommCardsList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="incomm-cards/:eligibleParticipantId"
                  element={
                    <ProtectedRoute requiredPermission="integrations:read">
                      <IncommCardDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="incomm-cards"
                  element={
                    <ProtectedRoute requiredPermission="integrations:read">
                      <IncommCardsSummary />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="paper-journal/uploads/:fileSha256"
                  element={
                    <ProtectedRoute requiredPermission="paper_journal:read">
                      <PaperJournalUploadDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="paper-journal"
                  element={
                    <ProtectedRoute requiredPermission="paper_journal:read">
                      <PaperJournalPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="paper-journal/uploads"
                  element={<Navigate to="/paper-journal" replace />}
                />
                <Route
                  path="paper-journal/lookup"
                  element={<Navigate to="/paper-journal?section=lookup" replace />}
                />
                <Route
                  path="marketing-banners/new"
                  element={
                    <ProtectedRoute requiredPermission="marketing_banner:write">
                      <MarketingBannerCreate />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="marketing-banners/:id/edit"
                  element={
                    <ProtectedRoute requiredPermission="marketing_banner:write">
                      <MarketingBannerEdit />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="marketing-banners"
                  element={
                    <ProtectedRoute requiredPermission="marketing_banner:read">
                      <MarketingBannerList />
                    </ProtectedRoute>
                  }
                />
                {/* Security routes */}
                <Route 
                  path="security/users" 
                  element={
                    <ProtectedRoute requiredPermission="security:read">
                      <SecurityUsers />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="security/roles" 
                  element={
                    <ProtectedRoute requiredPermission="security:read">
                      <SecurityRoles />
                    </ProtectedRoute>
                  } 
                />
                {/* Other placeholder routes */}
                <Route path="analytics" element={<div style={{ padding: '2rem', color: 'var(--color-text-primary)' }}>Analytics page - Coming soon</div>} />
              </Route>
            </Routes>
            </HeaderActionsProvider>
          </HashRouter>
        </AuthProvider>
      </ThemeProvider>
    </Provider>
  );
};

export default App;

